// Screen: "Admin"
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminGetUsers, adminGetAIStats, adminUpdateProfile, adminSetBalance, adminResetPassword, adminDeleteUser, adminSetMembership, adminGetReports, adminActionReport, getSeasons, adminCreateSeason, adminStartSeason, adminEndSeason, getAdminDashboard, getAdminSignupTrend, getAntiCheatFlags, getTurnedUpStats, adminGetPromoCodes, adminCreatePromoCode, adminUpdatePromoCode, getEconomyHealth, adminGetTransactions, adminGetTransactionSummary, adminGetPromoUsage, adminGetAdRewards, adminGetMemberships } from '../apiClient';

const inputStyle = {
  width: '100%', padding: '8px 10px', background: '#0d1117', border: '1px solid #30363d',
  borderRadius: 6, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', outline: 'none'
};

const thStyle = { textAlign: 'left', padding: '8px 8px', color: '#8b949e', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };

const actionBtnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 4,
  padding: '3px 8px', fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap'
});

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [aiPlayers, setAiPlayers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Modal state
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [balanceModal, setBalanceModal] = useState(null);
  const [balanceForm, setBalanceForm] = useState({ playMoneyBalance: '', realMoneyBalance: '', reason: '' });
  const [passwordModal, setPasswordModal] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [deleteModal, setDeleteModal] = useState(null);
  const [membershipModal, setMembershipModal] = useState(null);
  const [membershipForm, setMembershipForm] = useState({ tier: 'free', expiresAt: '' });
  const [seasonsList, setSeasonsList] = useState([]);
  const [seasonForm, setSeasonForm] = useState({ name: '', theme: '', startDate: '', endDate: '' });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardRefreshedAt, setDashboardRefreshedAt] = useState(null);
  const [signupTrend, setSignupTrend] = useState([]);
  const [antiCheatFlags, setAntiCheatFlags] = useState([]);
  const [turnedUpStats, setTurnedUpStats] = useState({ suits: [], total: 0 });
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: '', description: '', bucksAmount: 100, dailyLimit: 1, maxTotalUses: '', expirationDate: '', grantsClubTier: false, clubTierExpiresAt: '' });
  const [promoUsage, setPromoUsage] = useState({}); // promoId → { loading, rows }
  const [economyData, setEconomyData] = useState(null);

  // Accounting state
  const [acctView, setAcctView] = useState('ledger'); // ledger, summary, promos, ads, memberships
  const [acctLedger, setAcctLedger] = useState({ transactions: [], total: 0, page: 1, totalPages: 0 });
  const [acctSummary, setAcctSummary] = useState([]);
  const [acctPromos, setAcctPromos] = useState({ uses: [], total: 0, page: 1, totalPages: 0 });
  const [acctAds, setAcctAds] = useState({ rewards: [], total: 0, page: 1, totalPages: 0, stats: {} });
  const [acctMembers, setAcctMembers] = useState({ members: [], purchases: [] });
  const [acctFilters, setAcctFilters] = useState({ reason: '', userId: '', dateFrom: '', dateTo: '' });
  const [acctPage, setAcctPage] = useState(1);

  const refreshUsers = () => {
    adminGetUsers(token).then(data => setUsers(data.users || []));
  };
  const refreshSeasons = () => {
    getSeasons().then(data => setSeasonsList(data.seasons || []));
  };
  const refreshPromos = () => {
    adminGetPromoCodes(token).then(data => setPromoCodes(data.promoCodes || []));
  };

  useEffect(() => {
    if (!user?.isAdmin) return;
    setLoading(true);
    if (tab === 'users') {
      adminGetUsers(token).then(data => {
        setUsers(data.users || []);
        setLoading(false);
      });
    } else if (tab === 'moderation') {
      adminGetReports(token).then(data => {
        setReports(data.reports || []);
        setLoading(false);
      });
    } else if (tab === 'seasons') {
      getSeasons().then(data => {
        setSeasonsList(data.seasons || []);
        setLoading(false);
      });
    } else if (tab === 'dashboard') {
      Promise.all([
        getAdminDashboard(token),
        getAdminSignupTrend(token),
        getTurnedUpStats(token)
      ]).then(([stats, trend, turnedUp]) => {
        setDashboardStats(stats);
        setSignupTrend(trend.trend || []);
        setTurnedUpStats(turnedUp || { suits: [], total: 0 });
        setDashboardRefreshedAt(new Date());
        setLoading(false);
      });
    } else if (tab === 'anticheat') {
      getAntiCheatFlags(token).then(data => {
        setAntiCheatFlags(data.multiAccounts || []);
        setLoading(false);
      });
    } else if (tab === 'promos') {
      adminGetPromoCodes(token).then(data => {
        setPromoCodes(data.promoCodes || []);
        setLoading(false);
      });
    } else if (tab === 'economy') {
      getEconomyHealth(token).then(data => {
        setEconomyData(data);
        setLoading(false);
      });
    } else if (tab === 'accounting') {
      Promise.all([
        adminGetTransactions(token, { page: 1, limit: 50 }),
        adminGetTransactionSummary(token),
      ]).then(([ledger, summary]) => {
        setAcctLedger(ledger);
        setAcctSummary(summary.summary || []);
        setAcctPage(1);
        setLoading(false);
      });
    } else {
      adminGetAIStats(token).then(data => {
        setAiPlayers(data.aiPlayers || []);
        setLoading(false);
      });
    }
  }, [tab, token, user]);

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  if (!user?.isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e2e8f0', fontFamily: "'Georgia', serif" }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Access Denied</h1>
          <p style={{ color: '#8b949e' }}>You do not have admin privileges.</p>
          <button onClick={() => navigate('/home')} style={{ marginTop: 16, background: '#238636', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 14, cursor: 'pointer' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Edit Profile
  const openEditModal = (u) => {
    setEditForm({
      email: u.email || '',
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      nickname: u.nickname || '',
      zipCode: u.zipCode || ''
    });
    setEditModal(u);
  };

  const handleSaveProfile = async () => {
    if (!editModal) return;
    const result = await adminUpdateProfile(token, editModal.userId, editForm);
    if (result.success) {
      showMsg(`Profile updated for ${editModal.nickname || editModal.email}`);
      setEditModal(null);
      refreshUsers();
    } else {
      showMsg(`Error: ${result.error}`);
    }
  };

  // Set Balance
  const openBalanceModal = (u) => {
    setBalanceForm({
      playMoneyBalance: (u.playMoneyBalance || 0).toFixed(2),
      realMoneyBalance: (u.realMoneyBalance || 0).toFixed(2),
      reason: ''
    });
    setBalanceModal(u);
  };

  const handleSetBalance = async () => {
    if (!balanceModal) return;
    const result = await adminSetBalance(
      token,
      balanceModal.userId,
      parseFloat(balanceForm.playMoneyBalance) || 0,
      parseFloat(balanceForm.realMoneyBalance) || 0,
      balanceForm.reason
    );
    if (result.success) {
      showMsg(`Balance set for ${balanceModal.nickname || balanceModal.email}`);
      setBalanceModal(null);
      refreshUsers();
    } else {
      showMsg(`Error: ${result.error}`);
    }
  };

  // Reset Password
  const openPasswordModal = (u) => {
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordModal(u);
  };

  const handleResetPassword = async () => {
    if (!passwordModal) return;
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMsg('Error: Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 4) {
      showMsg('Error: Password must be at least 4 characters');
      return;
    }
    const result = await adminResetPassword(token, passwordModal.userId, passwordForm.newPassword);
    if (result.success) {
      showMsg(`Password reset for ${passwordModal.nickname || passwordModal.email}`);
      setPasswordModal(null);
    } else {
      showMsg(`Error: ${result.error}`);
    }
  };

  // Delete User
  const handleDeleteUser = async () => {
    if (!deleteModal) return;
    const result = await adminDeleteUser(token, deleteModal.userId);
    if (result.success) {
      showMsg(`Deleted user: ${deleteModal.nickname || deleteModal.email}`);
      setDeleteModal(null);
      refreshUsers();
    } else {
      showMsg(`Error: ${result.error}`);
    }
  };

  // Set Membership
  const openMembershipModal = (u) => {
    setMembershipForm({ tier: u.membershipTier || 'free', expiresAt: '' });
    setMembershipModal(u);
  };

  const handleSetMembership = async () => {
    if (!membershipModal) return;
    const result = await adminSetMembership(token, membershipModal.userId, membershipForm.tier, membershipForm.expiresAt || null);
    if (result.success) {
      showMsg(`${membershipModal.nickname || membershipModal.email} set to ${membershipForm.tier === 'club_member' ? 'Club Member' : 'Free'}`);
      setMembershipModal(null);
      refreshUsers();
    } else {
      showMsg(`Error: ${result.error}`);
    }
  };

  // Modal backdrop
  const Backdrop = ({ children, onClose }) => (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={onClose}>
      <div style={{
        background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
        padding: 28, maxWidth: 420, width: '90%'
      }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', padding: '40px 24px', fontFamily: "'Georgia', serif", color: '#e2e8f0' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Admin Panel</h1>
          <button onClick={() => navigate('/home')} style={{ background: 'transparent', border: '1px solid #30363d', color: '#58a6ff', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            Back to Home
          </button>
        </div>

        {/* Success/Error message */}
        {message && (
          <div style={{ background: message.startsWith('Error') ? '#3d1f1f' : '#1c3d2a', border: `1px solid ${message.startsWith('Error') ? '#f85149' : '#3fb950'}`, color: message.startsWith('Error') ? '#f85149' : '#3fb950', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {message}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #30363d' }}>
          {['dashboard', 'users', 'ai', 'moderation', 'seasons', 'anticheat', 'promos', 'economy', 'accounting'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: tab === t ? '#161b22' : 'transparent',
              border: 'none',
              borderBottom: tab === t ? '2px solid #58a6ff' : '2px solid transparent',
              color: tab === t ? '#e2e8f0' : '#8b949e',
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}>
              {{ dashboard: 'Dashboard', users: 'Users', ai: 'AI', moderation: 'Reports', seasons: 'Seasons', anticheat: 'Anti-Cheat', promos: 'Promos', economy: 'Economy' }[t]}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>Loading...</div>
        ) : tab === 'dashboard' ? (
          /* Dashboard */
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#484f58' }}>
                {dashboardRefreshedAt ? `Last refreshed: ${dashboardRefreshedAt.toLocaleTimeString()}` : ''}
                <span style={{ marginLeft: 8, fontSize: 10, color: '#30363d' }}>• Games This Week resets Monday</span>
              </div>
              <button
                onClick={() => {
                  setLoading(true);
                  Promise.all([getAdminDashboard(token), getAdminSignupTrend(token), getTurnedUpStats(token)])
                    .then(([stats, trend, turnedUp]) => {
                      setDashboardStats(stats);
                      setSignupTrend(trend.trend || []);
                      setTurnedUpStats(turnedUp || { suits: [], total: 0 });
                      setDashboardRefreshedAt(new Date());
                      setLoading(false);
                    });
                }}
                style={{ background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}
              >
                ↻ Refresh
              </button>
            </div>
            {dashboardStats ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Total Users', value: dashboardStats.totalUsers, color: '#58a6ff' },
                    { label: 'Unique IPs', value: dashboardStats.uniqueIPs ?? '—', color: '#38bdf8', note: 'distinct login locations' },
                    { label: 'DAU', value: dashboardStats.dau, color: '#3fb950' },
                    { label: 'WAU', value: dashboardStats.wau, color: '#3fb950', note: 'rolling 7d' },
                    { label: 'MAU', value: dashboardStats.mau, color: '#3fb950', note: 'rolling 30d' },
                    { label: 'Games Today', value: dashboardStats.gamesToday, color: '#f59e0b' },
                    { label: 'Games This Week', value: dashboardStats.gamesThisWeek, color: '#f59e0b', note: 'Mon–now' },
                    { label: 'Completed This Week', value: dashboardStats.completedGamesThisWeek ?? '—', color: '#4ade80', note: 'Mon–now' },
                    { label: 'Signups This Week', value: dashboardStats.signupsThisWeek, color: '#a78bfa', note: 'Mon–now' },
                    { label: 'Total Games (All Time)', value: (dashboardStats.totalGames || 0).toLocaleString(), color: '#fb923c' },
                    { label: 'Buck Euchre Games', value: (dashboardStats.totalBuckGames || 0).toLocaleString(), color: '#fb923c' },
                    { label: 'Euchre Games', value: (dashboardStats.totalEuchreGames || 0).toLocaleString(), color: '#fb923c' },
                    { label: 'Bucks in Circulation', value: (dashboardStats.totalBucks || 0).toLocaleString(), color: '#d4af37', note: 'current balances' },
                    { label: 'Total Bucks Distributed', value: (dashboardStats.totalBucksEverDistributed || 0).toLocaleString(), color: '#d4af37', note: 'all-time' },
                    { label: 'Avg Balance', value: (dashboardStats.avgBalance || 0).toLocaleString(), color: '#d4af37' },
                  ].map((s, i) => (
                    <div key={i} style={{
                      background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
                      padding: 12, textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#8b949e', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                      {s.note && <div style={{ fontSize: 9, color: '#484f58', marginTop: 2 }}>{s.note}</div>}
                    </div>
                  ))}
                </div>
                {/* Tier breakdown */}
                {dashboardStats.tierBreakdown && (
                  <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                    <h3 style={{ fontSize: 14, marginBottom: 10 }}>Membership Tiers</h3>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {Object.entries(dashboardStats.tierBreakdown).map(([tier, count]) => (
                        <div key={tier} style={{ fontSize: 13 }}>
                          <span style={{ color: tier === 'club_member' ? '#3fb950' : '#8b949e', fontWeight: 600 }}>
                            {tier === 'club_member' ? 'Club Member' : tier.replace('_', ' ')}
                          </span>: {count}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Signup trend */}
                {signupTrend.length > 0 && (
                  <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, padding: 16 }}>
                    <h3 style={{ fontSize: 14, marginBottom: 10 }}>Signups (Last 30 Days)</h3>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
                      {signupTrend.map((d, i) => {
                        const max = Math.max(...signupTrend.map(x => x.count), 1);
                        return (
                          <div key={i} title={`${d.day}: ${d.count}`} style={{
                            flex: 1, background: '#238636', borderRadius: '2px 2px 0 0',
                            height: `${(d.count / max) * 100}%`, minHeight: 2
                          }} />
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                      <span style={{ fontSize: 9, color: '#484f58' }}>{signupTrend[0]?.day}</span>
                      <span style={{ fontSize: 9, color: '#484f58' }}>{signupTrend[signupTrend.length - 1]?.day}</span>
                    </div>
                  </div>
                )}
                {/* Turned-Up Card Suit Distribution */}
                {turnedUpStats.total > 0 && (
                  <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, padding: 16, marginTop: 16 }}>
                    <h3 style={{ fontSize: 14, marginBottom: 12 }}>Turned-Up Card Suit Distribution</h3>
                    <p style={{ fontSize: 11, color: '#8b949e', marginBottom: 14, marginTop: 0 }}>
                      First trump option card across all recorded hands ({turnedUpStats.total.toLocaleString()} total)
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                      {(() => {
                        const suitConfig = {
                          spades:   { symbol: '\u2660', color: '#e2e8f0', bg: '#1a2332', border: '#58a6ff', label: 'Spades' },
                          hearts:   { symbol: '\u2665', color: '#f85149', bg: '#1a1215', border: '#f8514966', label: 'Hearts' },
                          clubs:    { symbol: '\u2663', color: '#3fb950', bg: '#121a15', border: '#3fb95066', label: 'Clubs' },
                          diamonds: { symbol: '\u2666', color: '#f59e0b', bg: '#1a1812', border: '#f59e0b66', label: 'Diamonds' }
                        };
                        // Ensure all 4 suits shown even if count is 0
                        const allSuits = ['spades', 'hearts', 'clubs', 'diamonds'];
                        const suitMap = {};
                        turnedUpStats.suits.forEach(s => { suitMap[s.suit] = s.count; });
                        return allSuits.map(suit => {
                          const count = suitMap[suit] || 0;
                          const pct = turnedUpStats.total > 0 ? ((count / turnedUpStats.total) * 100).toFixed(1) : '0.0';
                          const cfg = suitConfig[suit];
                          const isSpades = suit === 'spades';
                          return (
                            <div key={suit} style={{
                              background: isSpades ? '#0d2137' : cfg.bg,
                              border: `2px solid ${isSpades ? '#58a6ff' : cfg.border}`,
                              borderRadius: 10,
                              padding: '14px 12px',
                              textAlign: 'center',
                              position: 'relative',
                              boxShadow: isSpades ? '0 0 12px #58a6ff33' : 'none'
                            }}>
                              {isSpades && (
                                <div style={{
                                  position: 'absolute', top: 6, right: 8,
                                  fontSize: 8, fontWeight: 700, color: '#58a6ff',
                                  background: '#58a6ff22', padding: '1px 6px', borderRadius: 4,
                                  textTransform: 'uppercase', letterSpacing: 0.5
                                }}>Tracked</div>
                              )}
                              <div style={{ fontSize: 28, color: cfg.color, lineHeight: 1 }}>{cfg.symbol}</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: cfg.color, marginTop: 6 }}>{cfg.label}</div>
                              <div style={{ fontSize: 20, fontWeight: 700, color: isSpades ? '#58a6ff' : '#e2e8f0', marginTop: 4 }}>
                                {count.toLocaleString()}
                              </div>
                              <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>{pct}%</div>
                              {/* Bar visualization */}
                              <div style={{ marginTop: 8, height: 4, background: '#21262d', borderRadius: 2, overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%',
                                  width: `${parseFloat(pct)}%`,
                                  background: isSpades ? '#58a6ff' : cfg.color,
                                  borderRadius: 2,
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>No data available</div>
            )}
          </div>
        ) : tab === 'anticheat' ? (
          /* Anti-Cheat */
          <div>
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>Multi-Account Detection</h2>
            {antiCheatFlags.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40, background: '#161b22', border: '1px solid #30363d', borderRadius: 8 }}>
                No suspicious accounts detected.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {antiCheatFlags.map((flag, i) => (
                  <div key={i} style={{
                    background: '#161b22', border: '1px solid #f8514966', borderRadius: 8,
                    padding: 14
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: '#f85149', fontSize: 12, fontWeight: 600 }}>
                        {flag.accountCount} accounts from same IP
                      </span>
                      <span style={{ color: '#484f58', fontSize: 11 }}>{flag.ip}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#c9d1d9' }}>
                      {flag.nicknames.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'moderation' ? (
          /* Moderation / Reports */
          <div>
            {reports.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>
                No pending reports. All clear!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reports.map(r => (
                  <div key={r.report_id} style={{
                    background: '#0d1117', border: '1px solid #30363d', borderRadius: 8,
                    padding: '14px 16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <span style={{ color: '#f85149', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                          {r.report_type}
                        </span>
                        <span style={{ color: '#8b949e', fontSize: 11, marginLeft: 10 }}>
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: r.status === 'pending' ? '#f0883e20' : '#3fb95020',
                        color: r.status === 'pending' ? '#f0883e' : '#3fb950'
                      }}>
                        {r.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#c9d1d9', marginBottom: 6 }}>
                      <span style={{ color: '#8b949e' }}>Reporter:</span> {r.reporter_nickname || 'Unknown'}
                      <span style={{ color: '#8b949e', marginLeft: 16 }}>Reported:</span>{' '}
                      <span style={{ color: '#f85149' }}>{r.reported_nickname || 'Unknown'}</span>
                    </div>
                    {r.reported_content && (
                      <div style={{
                        background: '#161b22', borderRadius: 6, padding: '8px 12px',
                        fontSize: 12, color: '#8b949e', marginBottom: 10, fontStyle: 'italic'
                      }}>
                        "{r.reported_content}"
                      </div>
                    )}
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={async () => {
                          const result = await adminActionReport(token, r.report_id, 'dismiss');
                          if (result.success) { showMsg('Report dismissed'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                        }} style={actionBtnStyle('#6b7280')}>Dismiss</button>
                        <button onClick={async () => {
                          const result = await adminActionReport(token, r.report_id, 'warn');
                          if (result.success) { showMsg('User warned'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                        }} style={actionBtnStyle('#f59e0b')}>Warn</button>
                        <button onClick={async () => {
                          const result = await adminActionReport(token, r.report_id, 'mute_24h');
                          if (result.success) { showMsg('User muted 24h'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                        }} style={actionBtnStyle('#f0883e')}>Mute 24h</button>
                        <button onClick={async () => {
                          const result = await adminActionReport(token, r.report_id, 'mute_7d');
                          if (result.success) { showMsg('User muted 7 days'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                        }} style={actionBtnStyle('#f0883e')}>Mute 7d</button>
                        <button onClick={async () => {
                          const result = await adminActionReport(token, r.report_id, 'ban_7d');
                          if (result.success) { showMsg('User banned 7 days'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                        }} style={actionBtnStyle('#dc2626')}>Ban 7d</button>
                        <button onClick={async () => {
                          if (window.confirm('Permanently ban this user?')) {
                            const result = await adminActionReport(token, r.report_id, 'ban_permanent');
                            if (result.success) { showMsg('User permanently banned'); adminGetReports(token).then(d => setReports(d.reports || [])); }
                          }
                        }} style={actionBtnStyle('#7f1d1d')}>Ban Permanent</button>
                      </div>
                    )}
                    {r.action_taken && (
                      <div style={{ fontSize: 11, color: '#8b949e', marginTop: 6 }}>
                        Action: <span style={{ color: '#f0883e', fontWeight: 600 }}>{r.action_taken}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'users' ? (
          /* Users Table */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #30363d' }}>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Nickname</th>
                  <th style={thStyle}>Location</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                  <th style={thStyle}>Tier</th>
                  <th style={thStyle}>Joined</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.userId} style={{ borderBottom: '1px solid #21262d' }}>
                    <td style={{ padding: '8px 8px', color: '#c9d1d9', fontSize: 12 }}>{u.email}</td>
                    <td style={{ padding: '8px 8px', color: '#c9d1d9', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td style={{ padding: '8px 8px', color: '#c9d1d9', fontSize: 12 }}>{u.nickname || '—'}</td>
                    <td style={{ padding: '8px 8px', color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>
                      {u.city && u.state ? `${u.city}, ${u.state}` : u.zipCode || '—'}
                    </td>
                    <td style={{ padding: '8px 8px', color: '#fbbf24', textAlign: 'right', fontSize: 12 }}>{(u.playMoneyBalance || 0).toLocaleString()}</td>
                    <td style={{ padding: '8px 8px', fontSize: 11 }}>
                      <span style={{ color: (u.membershipTier || 'free') === 'club_member' ? '#3fb950' : '#8b949e', fontWeight: 600 }}>
                        {(u.membershipTier || 'free') === 'club_member' ? 'Club' : 'Free'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 8px', color: '#8b949e', fontSize: 10, whiteSpace: 'nowrap' }}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => openEditModal(u)} style={actionBtnStyle('#2563eb')}>Edit</button>
                        <button onClick={() => openBalanceModal(u)} style={actionBtnStyle('#238636')}>Balance</button>
                        <button onClick={() => openMembershipModal(u)} style={actionBtnStyle('#d97706')}>Tier</button>
                        <button onClick={() => openPasswordModal(u)} style={actionBtnStyle('#7c3aed')}>Password</button>
                        {!u.isAdmin && <button onClick={() => setDeleteModal(u)} style={actionBtnStyle('#dc2626')}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === 'seasons' ? (
          /* Seasons Management */
          <div>
            {/* Create Season Form */}
            <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <h3 style={{ color: '#e2e8f0', fontSize: 16, marginBottom: 12 }}>Create Season</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <input placeholder="Season name" value={seasonForm.name} onChange={e => setSeasonForm(prev => ({ ...prev, name: e.target.value }))} style={inputStyle} />
                <input placeholder="Theme (optional)" value={seasonForm.theme} onChange={e => setSeasonForm(prev => ({ ...prev, theme: e.target.value }))} style={inputStyle} />
                <input type="date" value={seasonForm.startDate} onChange={e => setSeasonForm(prev => ({ ...prev, startDate: e.target.value }))} style={inputStyle} />
                <input type="date" value={seasonForm.endDate} onChange={e => setSeasonForm(prev => ({ ...prev, endDate: e.target.value }))} style={inputStyle} />
              </div>
              <button onClick={async () => {
                if (!seasonForm.name || !seasonForm.startDate || !seasonForm.endDate) return;
                const result = await adminCreateSeason(token, seasonForm.name, seasonForm.theme, seasonForm.startDate, seasonForm.endDate);
                if (result.success) {
                  showMsg('Season created');
                  setSeasonForm({ name: '', theme: '', startDate: '', endDate: '' });
                  refreshSeasons();
                } else {
                  showMsg(result.error || 'Failed');
                }
              }} style={{ background: '#238636', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Create Season
              </button>
            </div>
            {seasonsList.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>No seasons created yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {seasonsList.map(s => (
                  <div key={s.season_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '12px 16px'
                  }}>
                    <div>
                      <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ color: '#8b949e', fontSize: 11 }}>
                        {s.start_date} to {s.end_date}
                        {s.theme && ` · ${s.theme}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px',
                        borderRadius: 4, background: s.status === 'active' ? '#1c3d2a' : s.status === 'upcoming' ? '#3d2f1f' : '#21262d',
                        color: s.status === 'active' ? '#3fb950' : s.status === 'upcoming' ? '#d29922' : '#8b949e'
                      }}>{s.status}</span>
                      {s.status === 'upcoming' && (
                        <button onClick={async () => {
                          if (!window.confirm(`Start season "${s.name}"? This will end any active season.`)) return;
                          const result = await adminStartSeason(token, s.season_id);
                          if (result.success) { showMsg('Season started'); refreshSeasons(); }
                        }} style={actionBtnStyle('#238636')}>Start</button>
                      )}
                      {s.status === 'active' && (
                        <button onClick={async () => {
                          if (!window.confirm(`End season "${s.name}"? This will snapshot all leaderboards.`)) return;
                          const result = await adminEndSeason(token, s.season_id);
                          if (result.success) { showMsg('Season ended & standings saved'); refreshSeasons(); }
                        }} style={actionBtnStyle('#da3633')}>End Season</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'promos' ? (
          /* Promo Codes Management */
          <div>
            {/* Create Promo Code Form */}
            <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <h3 style={{ color: '#e2e8f0', fontSize: 16, marginBottom: 12 }}>Create Promo Code</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Code (auto-uppercase)</label>
                  <input
                    placeholder="e.g. CLUBFREE30"
                    value={promoForm.code}
                    onChange={e => setPromoForm(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: 2 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Description</label>
                  <input
                    placeholder="Optional description"
                    value={promoForm.description}
                    onChange={e => setPromoForm(prev => ({ ...prev, description: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Bucks Amount {promoForm.grantsClubTier && <span style={{ color: '#484f58' }}>(0 = no Bucks)</span>}</label>
                  <input
                    type="number" min="0"
                    value={promoForm.bucksAmount}
                    onChange={e => setPromoForm(prev => ({ ...prev, bucksAmount: parseInt(e.target.value) || 0 }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Daily Limit (per user)</label>
                  <input
                    type="number" min="1"
                    value={promoForm.dailyLimit}
                    onChange={e => setPromoForm(prev => ({ ...prev, dailyLimit: parseInt(e.target.value) || 1 }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Max Total Uses (blank = unlimited)</label>
                  <input
                    type="number" min="1"
                    placeholder="Unlimited"
                    value={promoForm.maxTotalUses}
                    onChange={e => setPromoForm(prev => ({ ...prev, maxTotalUses: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Code Expiration Date</label>
                  <input
                    type="date"
                    value={promoForm.expirationDate}
                    onChange={e => setPromoForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Club Tier Section */}
              <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, padding: '12px 14px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: promoForm.grantsClubTier ? 10 : 0 }}>
                  <div
                    onClick={() => setPromoForm(prev => ({ ...prev, grantsClubTier: !prev.grantsClubTier, clubTierExpiresAt: '' }))}
                    style={{ width: 36, height: 20, borderRadius: 10, background: promoForm.grantsClubTier ? '#7c3aed' : '#4b5563', cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}
                  >
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: promoForm.grantsClubTier ? 18 : 3, transition: 'left 0.2s' }} />
                  </div>
                  <span style={{ color: promoForm.grantsClubTier ? '#a78bfa' : '#8b949e', fontSize: 13, fontWeight: 600 }}>
                    Grant Club Tier Membership
                  </span>
                  {promoForm.grantsClubTier && <span style={{ fontSize: 10, background: '#2d1b69', color: '#a78bfa', padding: '2px 8px', borderRadius: 10, border: '1px solid #7c3aed' }}>Club</span>}
                </div>
                {promoForm.grantsClubTier && (
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 11, marginBottom: 4 }}>Club Tier Expires At (user's membership expires this date)</label>
                    <input
                      type="date"
                      value={promoForm.clubTierExpiresAt}
                      onChange={e => setPromoForm(prev => ({ ...prev, clubTierExpiresAt: e.target.value }))}
                      style={{ ...inputStyle, borderColor: '#7c3aed' }}
                    />
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                      This is when the user's Club membership expires — can be different from the code's expiration date.
                    </div>
                  </div>
                )}
              </div>

              <button onClick={async () => {
                if (!promoForm.code || !promoForm.expirationDate) {
                  showMsg('Error: Code and Expiration Date are required');
                  return;
                }
                if (!promoForm.grantsClubTier && !promoForm.bucksAmount) {
                  showMsg('Error: Enter a Bucks Amount or enable Club Tier grant');
                  return;
                }
                if (promoForm.grantsClubTier && !promoForm.clubTierExpiresAt) {
                  showMsg('Error: Club Tier Expires At date is required when granting club tier');
                  return;
                }
                const payload = {
                  code: promoForm.code,
                  description: promoForm.description,
                  bucksAmount: promoForm.bucksAmount || 0,
                  dailyLimit: promoForm.dailyLimit,
                  maxTotalUses: promoForm.maxTotalUses ? parseInt(promoForm.maxTotalUses) : null,
                  expirationDate: promoForm.expirationDate,
                  grantsClubTier: promoForm.grantsClubTier,
                  clubTierExpiresAt: promoForm.clubTierExpiresAt || null,
                };
                const result = await adminCreatePromoCode(token, payload);
                if (result.success) {
                  showMsg(`Promo code "${promoForm.code}" created!`);
                  setPromoForm({ code: '', description: '', bucksAmount: 100, dailyLimit: 1, maxTotalUses: '', expirationDate: '', grantsClubTier: false, clubTierExpiresAt: '' });
                  refreshPromos();
                } else {
                  showMsg(`Error: ${result.error || 'Failed to create promo code'}`);
                }
              }} style={{ background: '#238636', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Create Code
              </button>
            </div>

            {/* Promo Codes List */}
            {promoCodes.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>No promo codes created yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #30363d' }}>
                      <th style={thStyle}>Code</th>
                      <th style={thStyle}>Description</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Club</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Daily Limit</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Uses / Max</th>
                      <th style={thStyle}>Code Expires</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map(p => {
                      const expired = new Date(p.expiration_date) < new Date();
                      const active = p.is_active && !expired;
                      return (
                      <React.Fragment key={p.promo_id}>
                        <tr style={{ borderBottom: '1px solid #21262d', opacity: active ? 1 : 0.5 }}>
                          <td style={{ padding: '8px 8px', color: '#e2e8f0', fontFamily: 'monospace', letterSpacing: 1, fontWeight: 600 }}>{p.code}</td>
                          <td style={{ padding: '8px 8px', color: '#8b949e', fontSize: 11, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description || '—'}</td>
                          <td style={{ padding: '8px 8px', color: '#fbbf24', textAlign: 'right', fontWeight: 600 }}>{(p.bucks_amount || 0).toLocaleString()}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            {p.grants_club_tier ? (
                              <div>
                                <span style={{ fontSize: 10, background: '#2d1b69', color: '#a78bfa', padding: '2px 6px', borderRadius: 8, border: '1px solid #7c3aed' }}>Club</span>
                                {p.club_tier_expires_at && <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>until {new Date(p.club_tier_expires_at).toLocaleDateString()}</div>}
                              </div>
                            ) : <span style={{ color: '#484f58' }}>—</span>}
                          </td>
                          <td style={{ padding: '8px 8px', color: '#c9d1d9', textAlign: 'center' }}>{p.daily_limit}</td>
                          <td style={{ padding: '8px 8px', color: '#c9d1d9', textAlign: 'center' }}>
                            {p.current_uses || 0}{p.max_total_uses ? ` / ${p.max_total_uses}` : ' / ∞'}
                          </td>
                          <td style={{ padding: '8px 8px', color: expired ? '#f85149' : '#8b949e', fontSize: 11 }}>
                            {p.expiration_date ? new Date(p.expiration_date).toLocaleDateString() : '—'}
                            {expired && <span style={{ marginLeft: 4, fontSize: 9, color: '#f85149' }}>(expired)</span>}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                              background: active ? '#1c3d2a' : '#3d1c1c',
                              color: active ? '#3fb950' : '#f85149'
                            }}>
                              {active ? 'Active' : expired ? 'Expired' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center', display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button
                              onClick={async () => {
                                const result = await adminUpdatePromoCode(token, p.promo_id, { isActive: p.is_active ? 0 : 1 });
                                if (result.success) {
                                  showMsg(p.is_active ? `"${p.code}" deactivated` : `"${p.code}" activated`);
                                  refreshPromos();
                                }
                              }}
                              style={actionBtnStyle(p.is_active ? '#da3633' : '#238636')}
                            >
                              {p.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={async () => {
                                const id = p.promo_id;
                                if (promoUsage[id]?.rows) { setPromoUsage(prev => ({ ...prev, [id]: undefined })); return; }
                                setPromoUsage(prev => ({ ...prev, [id]: { loading: true } }));
                                const res = await fetch(`/api/admin/promo-codes/${id}/usage`, { headers: { Authorization: `Bearer ${token}` } });
                                const data = await res.json();
                                setPromoUsage(prev => ({ ...prev, [id]: { rows: data.usage || [] } }));
                              }}
                              style={{ ...actionBtnStyle('#1d4ed8'), fontSize: 10 }}
                            >
                              {promoUsage[p.promo_id]?.rows ? 'Hide' : 'Who Used?'}
                            </button>
                          </td>
                        </tr>
                        {promoUsage[p.promo_id]?.rows && (
                          <tr key={p.promo_id + '_usage'} style={{ background: '#0d1117', borderBottom: '1px solid #21262d' }}>
                            <td colSpan={9} style={{ padding: '8px 16px' }}>
                              {promoUsage[p.promo_id].loading ? (
                                <span style={{ color: '#8b949e', fontSize: 11 }}>Loading...</span>
                              ) : promoUsage[p.promo_id].rows.length === 0 ? (
                                <span style={{ color: '#8b949e', fontSize: 11, fontStyle: 'italic' }}>No redemptions yet.</span>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ color: '#8b949e' }}>
                                      <th style={{ textAlign: 'left', padding: '2px 8px' }}>User</th>
                                      <th style={{ textAlign: 'left', padding: '2px 8px' }}>Nickname</th>
                                      <th style={{ textAlign: 'right', padding: '2px 8px' }}>Bucks</th>
                                      <th style={{ textAlign: 'left', padding: '2px 8px' }}>Redeemed At</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {promoUsage[p.promo_id].rows.map((u, i) => (
                                      <tr key={i} style={{ borderTop: '1px solid #21262d' }}>
                                        <td style={{ padding: '3px 8px', color: '#8b949e' }}>{u.email || u.user_id}</td>
                                        <td style={{ padding: '3px 8px', color: '#e2e8f0' }}>{u.nickname || '—'}</td>
                                        <td style={{ padding: '3px 8px', color: '#fbbf24', textAlign: 'right' }}>{u.bucks_awarded}</td>
                                        <td style={{ padding: '3px 8px', color: '#484f58' }}>{u.redeemed_at ? new Date(u.redeemed_at).toLocaleString() : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}

                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : tab === 'accounting' ? (
          /* Accounting / Currency Tracker */
          <div>
            {/* Sub-navigation */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #21262d' }}>
              {[
                { key: 'ledger', label: 'Transaction Ledger' },
                { key: 'summary', label: 'Summary' },
                { key: 'promos', label: 'Promo Codes' },
                { key: 'ads', label: 'Ad Rewards' },
                { key: 'memberships', label: 'Memberships' },
              ].map(v => (
                <button key={v.key} onClick={() => {
                  setAcctView(v.key);
                  if (v.key === 'promos' && acctPromos.uses.length === 0) {
                    adminGetPromoUsage(token, { page: 1, limit: 50 }).then(setAcctPromos);
                  }
                  if (v.key === 'ads' && acctAds.rewards.length === 0) {
                    adminGetAdRewards(token, { page: 1, limit: 50 }).then(setAcctAds);
                  }
                  if (v.key === 'memberships' && acctMembers.members.length === 0) {
                    adminGetMemberships(token).then(setAcctMembers);
                  }
                }} style={{
                  background: acctView === v.key ? '#161b22' : 'transparent',
                  border: 'none', borderBottom: acctView === v.key ? '2px solid #d97706' : '2px solid transparent',
                  color: acctView === v.key ? '#fbbf24' : '#8b949e',
                  padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600
                }}>{v.label}</button>
              ))}
            </div>

            {/* === TRANSACTION LEDGER === */}
            {acctView === 'ledger' && (
              <div>
                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 10, marginBottom: 2 }}>Reason</label>
                    <select value={acctFilters.reason} onChange={e => setAcctFilters(f => ({ ...f, reason: e.target.value }))}
                      style={{ ...inputStyle, width: 160, fontSize: 12, padding: '5px 8px' }}>
                      <option value="">All Types</option>
                      <option value="game_payout">Game Payouts</option>
                      <option value="promo_code">Promo Codes</option>
                      <option value="ad_reward">Ad Rewards</option>
                      <option value="Daily login">Daily Login</option>
                      <option value="onboarding">Onboarding</option>
                      <option value="email_verification">Email Verify</option>
                      <option value="First week">First Week</option>
                      <option value="Tip">Tips</option>
                      <option value="Admin">Admin</option>
                      <option value="forfeited">Forfeit</option>
                      <option value="abandoned">Abandoned</option>
                      <option value="Season">Season Rewards</option>
                      <option value="Purchased">Shop</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 10, marginBottom: 2 }}>From</label>
                    <input type="date" value={acctFilters.dateFrom} onChange={e => setAcctFilters(f => ({ ...f, dateFrom: e.target.value }))}
                      style={{ ...inputStyle, width: 130, fontSize: 12, padding: '5px 8px' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 10, marginBottom: 2 }}>To</label>
                    <input type="date" value={acctFilters.dateTo} onChange={e => setAcctFilters(f => ({ ...f, dateTo: e.target.value }))}
                      style={{ ...inputStyle, width: 130, fontSize: 12, padding: '5px 8px' }} />
                  </div>
                  <button onClick={() => {
                    const params = { page: 1, limit: 50 };
                    if (acctFilters.reason) params.reason = acctFilters.reason;
                    if (acctFilters.dateFrom) params.dateFrom = acctFilters.dateFrom;
                    if (acctFilters.dateTo) params.dateTo = acctFilters.dateTo;
                    adminGetTransactions(token, params).then(data => { setAcctLedger(data); setAcctPage(1); });
                  }} style={actionBtnStyle('#238636')}>Search</button>
                  <button onClick={() => {
                    setAcctFilters({ reason: '', userId: '', dateFrom: '', dateTo: '' });
                    adminGetTransactions(token, { page: 1, limit: 50 }).then(data => { setAcctLedger(data); setAcctPage(1); });
                  }} style={actionBtnStyle('#4b5563')}>Clear</button>
                  <span style={{ color: '#8b949e', fontSize: 11, marginLeft: 8 }}>{acctLedger.total.toLocaleString()} transactions</span>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #30363d' }}>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Player</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Before</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>After</th>
                        <th style={thStyle}>Reason</th>
                        <th style={thStyle}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acctLedger.transactions.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No transactions found.</td></tr>
                      ) : acctLedger.transactions.map((tx, i) => (
                        <tr key={tx.transaction_id || i} style={{ borderBottom: '1px solid #161b22' }}>
                          <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {tx.created_at ? new Date(tx.created_at + 'Z').toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#c9d1d9' }}>
                            {tx.nickname || tx.email || tx.user_id?.slice(0, 8)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: tx.amount_change > 0 ? '#3fb950' : tx.amount_change < 0 ? '#f85149' : '#8b949e' }}>
                            {tx.amount_change > 0 ? '+' : ''}{(tx.amount_change || 0).toFixed(1)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#8b949e', fontSize: 11 }}>
                            {(tx.balance_before || 0).toFixed(0)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#8b949e', fontSize: 11 }}>
                            {(tx.balance_after || 0).toFixed(0)}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#c9d1d9', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {tx.reason}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <span style={{
                              background: tx.wager_mode === 'play' ? '#1c3d2a' : '#3d1f1f',
                              color: tx.wager_mode === 'play' ? '#3fb950' : '#f85149',
                              padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600
                            }}>{tx.wager_mode}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {acctLedger.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                    <button disabled={acctPage <= 1} onClick={() => {
                      const p = acctPage - 1;
                      const params = { page: p, limit: 50 };
                      if (acctFilters.reason) params.reason = acctFilters.reason;
                      if (acctFilters.dateFrom) params.dateFrom = acctFilters.dateFrom;
                      if (acctFilters.dateTo) params.dateTo = acctFilters.dateTo;
                      adminGetTransactions(token, params).then(data => { setAcctLedger(data); setAcctPage(p); });
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctPage <= 1 ? 0.4 : 1 }}>Prev</button>
                    <span style={{ color: '#8b949e', fontSize: 12, lineHeight: '24px' }}>Page {acctPage} of {acctLedger.totalPages}</span>
                    <button disabled={acctPage >= acctLedger.totalPages} onClick={() => {
                      const p = acctPage + 1;
                      const params = { page: p, limit: 50 };
                      if (acctFilters.reason) params.reason = acctFilters.reason;
                      if (acctFilters.dateFrom) params.dateFrom = acctFilters.dateFrom;
                      if (acctFilters.dateTo) params.dateTo = acctFilters.dateTo;
                      adminGetTransactions(token, params).then(data => { setAcctLedger(data); setAcctPage(p); });
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctPage >= acctLedger.totalPages ? 0.4 : 1 }}>Next</button>
                  </div>
                )}
              </div>
            )}

            {/* === SUMMARY === */}
            {acctView === 'summary' && (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 10, marginBottom: 2 }}>From</label>
                    <input type="date" value={acctFilters.dateFrom} onChange={e => setAcctFilters(f => ({ ...f, dateFrom: e.target.value }))}
                      style={{ ...inputStyle, width: 130, fontSize: 12, padding: '5px 8px' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#8b949e', fontSize: 10, marginBottom: 2 }}>To</label>
                    <input type="date" value={acctFilters.dateTo} onChange={e => setAcctFilters(f => ({ ...f, dateTo: e.target.value }))}
                      style={{ ...inputStyle, width: 130, fontSize: 12, padding: '5px 8px' }} />
                  </div>
                  <button onClick={() => {
                    const params = {};
                    if (acctFilters.dateFrom) params.dateFrom = acctFilters.dateFrom;
                    if (acctFilters.dateTo) params.dateTo = acctFilters.dateTo;
                    adminGetTransactionSummary(token, params).then(data => setAcctSummary(data.summary || []));
                  }} style={actionBtnStyle('#238636')}>Refresh</button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #30363d' }}>
                      <th style={thStyle}>Category</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Transactions</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Bucks In</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Bucks Out</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctSummary.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No data.</td></tr>
                    ) : acctSummary.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #161b22' }}>
                        <td style={{ padding: '8px', color: '#e2e8f0', fontWeight: 600 }}>{row.category}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#c9d1d9' }}>{(row.txn_count || 0).toLocaleString()}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#3fb950', fontWeight: 600 }}>+{(row.total_in || 0).toFixed(0)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#f85149', fontWeight: 600 }}>{(row.total_out || 0).toFixed(0)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: (row.net || 0) >= 0 ? '#3fb950' : '#f85149' }}>
                          {(row.net || 0) >= 0 ? '+' : ''}{(row.net || 0).toFixed(0)}
                        </td>
                      </tr>
                    ))}
                    {acctSummary.length > 0 && (
                      <tr style={{ borderTop: '2px solid #30363d' }}>
                        <td style={{ padding: '8px', color: '#fbbf24', fontWeight: 700 }}>TOTAL</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#fbbf24', fontWeight: 700 }}>
                          {acctSummary.reduce((s, r) => s + (r.txn_count || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#3fb950', fontWeight: 700 }}>
                          +{acctSummary.reduce((s, r) => s + (r.total_in || 0), 0).toFixed(0)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#f85149', fontWeight: 700 }}>
                          {acctSummary.reduce((s, r) => s + (r.total_out || 0), 0).toFixed(0)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#fbbf24', fontWeight: 700 }}>
                          {acctSummary.reduce((s, r) => s + (r.net || 0), 0) >= 0 ? '+' : ''}
                          {acctSummary.reduce((s, r) => s + (r.net || 0), 0).toFixed(0)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* === PROMO CODE USAGE === */}
            {acctView === 'promos' && (
              <div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #30363d' }}>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Player</th>
                        <th style={thStyle}>Code</th>
                        <th style={thStyle}>Description</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acctPromos.uses.length === 0 ? (
                        <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No promo code redemptions yet.</td></tr>
                      ) : acctPromos.uses.map((u, i) => (
                        <tr key={u.usage_id || i} style={{ borderBottom: '1px solid #161b22' }}>
                          <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {u.redeemed_at ? new Date(u.redeemed_at + 'Z').toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#c9d1d9' }}>{u.nickname || u.email || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <span style={{ background: '#1c3d2a', color: '#3fb950', padding: '2px 8px', borderRadius: 4, fontWeight: 700, fontSize: 11, fontFamily: 'monospace' }}>{u.code}</span>
                          </td>
                          <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11 }}>{u.description || '—'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3fb950', fontWeight: 700 }}>+{u.bucks_awarded}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {acctPromos.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                    <button disabled={acctPromos.page <= 1} onClick={() => {
                      adminGetPromoUsage(token, { page: acctPromos.page - 1, limit: 50 }).then(setAcctPromos);
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctPromos.page <= 1 ? 0.4 : 1 }}>Prev</button>
                    <span style={{ color: '#8b949e', fontSize: 12, lineHeight: '24px' }}>Page {acctPromos.page} of {acctPromos.totalPages} ({acctPromos.total} total)</span>
                    <button disabled={acctPromos.page >= acctPromos.totalPages} onClick={() => {
                      adminGetPromoUsage(token, { page: acctPromos.page + 1, limit: 50 }).then(setAcctPromos);
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctPromos.page >= acctPromos.totalPages ? 0.4 : 1 }}>Next</button>
                  </div>
                )}
              </div>
            )}

            {/* === AD REWARDS === */}
            {acctView === 'ads' && (
              <div>
                {/* Summary cards */}
                {acctAds.stats && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Total Claims', value: (acctAds.stats.totalClaims || 0).toLocaleString(), color: '#58a6ff' },
                      { label: 'Total Bucks Awarded', value: (acctAds.stats.totalAwarded || 0).toLocaleString(), color: '#3fb950' },
                      { label: 'Unique Users', value: (acctAds.stats.uniqueUsers || 0).toLocaleString(), color: '#fbbf24' },
                    ].map(c => (
                      <div key={c.label} style={{
                        background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
                        padding: '12px 16px', textAlign: 'center', minWidth: 120
                      }}>
                        <div style={{ fontSize: 10, color: '#8b949e', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>{c.label}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #30363d' }}>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Player</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Balance After</th>
                        <th style={thStyle}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acctAds.rewards.length === 0 ? (
                        <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No ad reward claims yet.</td></tr>
                      ) : acctAds.rewards.map((r, i) => (
                        <tr key={r.transaction_id || i} style={{ borderBottom: '1px solid #161b22' }}>
                          <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {r.created_at ? new Date(r.created_at + 'Z').toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#c9d1d9' }}>{r.nickname || r.email || '—'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3fb950', fontWeight: 700 }}>+{(r.amount_change || 0).toFixed(0)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#8b949e' }}>{(r.balance_after || 0).toFixed(0)}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <span style={{
                              background: r.reason === 'ad_reward' ? '#1c3d2a' : '#2d1f3d',
                              color: r.reason === 'ad_reward' ? '#3fb950' : '#a78bfa',
                              padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600
                            }}>{r.reason === 'ad_reward' ? 'Ad Watch' : 'Ad Recovery'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {acctAds.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                    <button disabled={acctAds.page <= 1} onClick={() => {
                      adminGetAdRewards(token, { page: acctAds.page - 1, limit: 50 }).then(setAcctAds);
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctAds.page <= 1 ? 0.4 : 1 }}>Prev</button>
                    <span style={{ color: '#8b949e', fontSize: 12, lineHeight: '24px' }}>Page {acctAds.page} of {acctAds.totalPages} ({acctAds.total} total)</span>
                    <button disabled={acctAds.page >= acctAds.totalPages} onClick={() => {
                      adminGetAdRewards(token, { page: acctAds.page + 1, limit: 50 }).then(setAcctAds);
                    }} style={{ ...actionBtnStyle('#21262d'), opacity: acctAds.page >= acctAds.totalPages ? 0.4 : 1 }}>Next</button>
                  </div>
                )}
              </div>
            )}

            {/* === MEMBERSHIPS === */}
            {acctView === 'memberships' && (
              <div>
                <h3 style={{ color: '#d97706', fontSize: 15, marginBottom: 12 }}>Active Club Members</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 24 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #30363d' }}>
                      <th style={thStyle}>Player</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Tier</th>
                      <th style={thStyle}>Subscription ID</th>
                      <th style={thStyle}>Expires</th>
                      <th style={thStyle}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctMembers.members.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No club members.</td></tr>
                    ) : acctMembers.members.map((m, i) => (
                      <tr key={m.user_id || i} style={{ borderBottom: '1px solid #161b22' }}>
                        <td style={{ padding: '6px 8px', color: '#c9d1d9', fontWeight: 600 }}>{m.nickname || '—'}</td>
                        <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11 }}>{m.email}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            background: m.membership_tier === 'club_member' ? '#1c3d2a' : '#21262d',
                            color: m.membership_tier === 'club_member' ? '#3fb950' : '#8b949e',
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600
                          }}>{m.membership_tier === 'club_member' ? 'Club Member' : 'Free'}</span>
                        </td>
                        <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 10, fontFamily: 'monospace' }}>{m.subscription_id || '—'}</td>
                        <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11 }}>
                          {m.subscription_expires ? new Date(m.subscription_expires).toLocaleDateString() : 'Never'}
                        </td>
                        <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11 }}>
                          {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {acctMembers.purchases.length > 0 && (
                  <>
                    <h3 style={{ color: '#d97706', fontSize: 15, marginBottom: 12 }}>Purchase History</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #30363d' }}>
                          <th style={thStyle}>Time</th>
                          <th style={thStyle}>Player</th>
                          <th style={thStyle}>Type</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>USD</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                          <th style={thStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {acctMembers.purchases.map((p, i) => (
                          <tr key={p.purchase_id || i} style={{ borderBottom: '1px solid #161b22' }}>
                            <td style={{ padding: '6px 8px', color: '#8b949e', fontSize: 11, whiteSpace: 'nowrap' }}>
                              {p.created_at ? new Date(p.created_at + 'Z').toLocaleString() : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', color: '#c9d1d9' }}>{p.nickname || p.email || '—'}</td>
                            <td style={{ padding: '6px 8px', color: '#c9d1d9' }}>{p.type}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3fb950', fontWeight: 700 }}>${(p.amount_usd || 0).toFixed(2)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#fbbf24' }}>{p.bucks_awarded || 0}</td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{
                                background: p.status === 'completed' ? '#1c3d2a' : '#3d1f1f',
                                color: p.status === 'completed' ? '#3fb950' : '#f85149',
                                padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600
                              }}>{p.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        ) : tab === 'economy' ? (
          /* Economy Health Dashboard */
          <div>
            {economyData ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Avg Bankroll', value: Math.round(economyData.avgBankroll || 0).toLocaleString(), color: (economyData.avgBankroll || 0) > 30 ? '#3fb950' : (economyData.avgBankroll || 0) > 15 ? '#fbbf24' : '#f85149' },
                  { label: 'Median Bankroll', value: Math.round(economyData.medianBankroll || 0).toLocaleString(), color: (economyData.medianBankroll || 0) > 25 ? '#3fb950' : (economyData.medianBankroll || 0) > 10 ? '#fbbf24' : '#f85149' },
                  { label: '% Broke (< 10)', value: ((economyData.brokePct || 0) * 100).toFixed(1) + '%', color: (economyData.brokePct || 0) < 0.15 ? '#3fb950' : (economyData.brokePct || 0) < 0.3 ? '#fbbf24' : '#f85149' },
                  { label: 'Gini Coefficient', value: (economyData.giniCoefficient || 0).toFixed(3), color: (economyData.giniCoefficient || 0) < 0.4 ? '#3fb950' : (economyData.giniCoefficient || 0) < 0.6 ? '#fbbf24' : '#f85149' },
                  { label: 'Daily Active Bucks', value: Math.round(economyData.dailyActiveBucks || 0).toLocaleString(), color: '#58a6ff' },
                  { label: 'Total Players', value: (economyData.totalPlayers || 0).toLocaleString(), color: '#8b949e' },
                ].map(card => (
                  <div key={card.label} style={{
                    background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
                    padding: '16px 18px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 11, color: '#8b949e', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>{card.label}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: card.color }}>{card.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#8b949e', padding: 40 }}>No economy data available.</div>
            )}

            {economyData && economyData.interventionFlags && economyData.interventionFlags.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <h3 style={{ color: '#f85149', fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Intervention Alerts</h3>
                {economyData.interventionFlags.map((flag, i) => (
                  <div key={i} style={{
                    background: '#3d1f1f', border: '1px solid #f85149', borderRadius: 8,
                    padding: '10px 14px', marginBottom: 8, fontSize: 13, color: '#f85149'
                  }}>
                    {flag}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* AI Players Table */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #30363d' }}>
                  <th style={thStyle}>Name</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Bucks</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Games</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Wins</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Losses</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Last Played</th>
                </tr>
              </thead>
              <tbody>
                {aiPlayers.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#8b949e' }}>No AI player data yet. Play some games first!</td></tr>
                ) : aiPlayers.map(ai => (
                  <tr key={ai.aiName} style={{ borderBottom: '1px solid #21262d' }}>
                    <td style={{ padding: '8px 12px', color: '#c9d1d9', fontWeight: 600 }}>{ai.aiName}</td>
                    <td style={{ padding: '8px 12px', color: ai.playMoney >= 0 ? '#fbbf24' : '#f85149', textAlign: 'right' }}>{(ai.playMoney || 0).toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', color: '#c9d1d9', textAlign: 'right' }}>{ai.gamesPlayed || 0}</td>
                    <td style={{ padding: '8px 12px', color: '#3fb950', textAlign: 'right' }}>{ai.wins || 0}</td>
                    <td style={{ padding: '8px 12px', color: '#f85149', textAlign: 'right' }}>{ai.losses || 0}</td>
                    <td style={{ padding: '8px 12px', color: '#8b949e', textAlign: 'right', fontSize: 11 }}>
                      {ai.lastPlayed ? new Date(ai.lastPlayed).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Profile Modal */}
        {editModal && (
          <Backdrop onClose={() => setEditModal(null)}>
            <h3 style={{ color: '#e2e8f0', fontSize: 18, marginBottom: 16 }}>
              Edit Profile: {editModal.nickname || editModal.email}
            </h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Email</label>
              <input type="text" value={editForm.email} onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>First Name</label>
                <input type="text" value={editForm.firstName} onChange={e => setEditForm(prev => ({ ...prev, firstName: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Last Name</label>
                <input type="text" value={editForm.lastName} onChange={e => setEditForm(prev => ({ ...prev, lastName: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Nickname</label>
                <input type="text" value={editForm.nickname} onChange={e => setEditForm(prev => ({ ...prev, nickname: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Zip Code</label>
                <input type="text" value={editForm.zipCode} onChange={e => setEditForm(prev => ({ ...prev, zipCode: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSaveProfile} style={{
                flex: 1, background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Save Profile</button>
              <button onClick={() => setEditModal(null)} style={{
                background: '#4b5563', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 16px', fontSize: 14, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </Backdrop>
        )}

        {/* Set Balance Modal */}
        {balanceModal && (
          <Backdrop onClose={() => setBalanceModal(null)}>
            <h3 style={{ color: '#e2e8f0', fontSize: 18, marginBottom: 8 }}>
              Set Balance: {balanceModal.nickname || balanceModal.email}
            </h3>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: '#8b949e' }}>
              <span>Current Bucks: <strong style={{ color: '#fbbf24' }}>{(balanceModal.playMoneyBalance || 0).toLocaleString()}</strong></span>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Bucks Balance</label>
              <input type="number" step="0.01" value={balanceForm.playMoneyBalance} onChange={e => setBalanceForm(prev => ({ ...prev, playMoneyBalance: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Reason</label>
              <input type="text" value={balanceForm.reason} onChange={e => setBalanceForm(prev => ({ ...prev, reason: e.target.value }))} style={inputStyle} placeholder="e.g. Manual adjustment" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSetBalance} style={{
                flex: 1, background: '#238636', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Set Balance</button>
              <button onClick={() => setBalanceModal(null)} style={{
                background: '#4b5563', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 16px', fontSize: 14, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </Backdrop>
        )}

        {/* Reset Password Modal */}
        {passwordModal && (
          <Backdrop onClose={() => setPasswordModal(null)}>
            <h3 style={{ color: '#e2e8f0', fontSize: 18, marginBottom: 16 }}>
              Reset Password: {passwordModal.nickname || passwordModal.email}
            </h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>New Password</label>
              <input type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))} style={inputStyle} placeholder="Enter new password" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Confirm Password</label>
              <input type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))} style={inputStyle} placeholder="Confirm new password" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleResetPassword} style={{
                flex: 1, background: '#7c3aed', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Reset Password</button>
              <button onClick={() => setPasswordModal(null)} style={{
                background: '#4b5563', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 16px', fontSize: 14, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </Backdrop>
        )}

        {/* Set Membership Modal */}
        {membershipModal && (
          <Backdrop onClose={() => setMembershipModal(null)}>
            <h3 style={{ color: '#d97706', fontSize: 18, marginBottom: 16 }}>
              Set Membership: {membershipModal.nickname || membershipModal.email}
            </h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Tier</label>
              <select
                value={membershipForm.tier}
                onChange={e => setMembershipForm(f => ({ ...f, tier: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="free">Free</option>
                <option value="club_member">Club Member ($4.99/mo)</option>
              </select>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Expires (optional)</label>
              <input
                type="date"
                value={membershipForm.expiresAt}
                onChange={e => setMembershipForm(f => ({ ...f, expiresAt: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSetMembership} style={{
                flex: 1, background: '#d97706', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Set Membership</button>
              <button onClick={() => setMembershipModal(null)} style={{
                background: '#4b5563', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 16px', fontSize: 14, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </Backdrop>
        )}

        {/* Delete User Confirmation Modal */}
        {deleteModal && (
          <Backdrop onClose={() => setDeleteModal(null)}>
            <h3 style={{ color: '#f85149', fontSize: 18, marginBottom: 16 }}>
              Delete User
            </h3>
            <p style={{ color: '#c9d1d9', marginBottom: 8 }}>
              Are you sure you want to permanently delete this account?
            </p>
            <p style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>
              {deleteModal.nickname || '—'} ({deleteModal.email})
            </p>
            <p style={{ color: '#8b949e', fontSize: 12, marginBottom: 20 }}>
              This will delete the user, their balance transactions, and saved games. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleDeleteUser} style={{
                flex: 1, background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Delete Permanently</button>
              <button onClick={() => setDeleteModal(null)} style={{
                background: '#4b5563', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 16px', fontSize: 14, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </Backdrop>
        )}
      </div>
    </div>
  );
}
